/**
 * SkinsLegend Behavior Tracker (sl-tracker.js)
 * Lightweight storefront analytics — tracks page views, sessions, UTMs,
 * scroll depth, e-commerce events, and exit behavior.
 *
 * Deploy: Inline this script in Shopify theme.liquid before closing body tag,
 * or load as external script from your backend.
 */
(function () {
  'use strict';

  // ─── Configuration ──────────────────────────────────────────
  var ENDPOINT = (window.__SL_TRACKER_URL || 'https://ops.skinslegend.com') + '/api/behavior';
  var COOKIE_NAME = 'sl_vid';
  var SESSION_KEY = 'sl_sid';
  var SESSION_TS_KEY = 'sl_sts';
  var UTM_KEY = 'sl_utm';
  var SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  var COOKIE_DAYS = 365;
  var FLUSH_INTERVAL = 5000; // 5 seconds
  var SCROLL_THRESHOLDS = [25, 50, 75, 100];

  // ─── State ──────────────────────────────────────────────────
  var visitorId, sessionId, deviceInfo, utmData, trafficSource;
  var customerData = null; // Shopify customer identity when logged in
  var fingerprintId = null; // ThumbmarkJS browser fingerprint
  var eventQueue = [];
  var scrollMilestones = {};
  var pageEntryTime = null;
  var currentPageUrl = null;
  var exitIntentFired = false;
  var initialized = false;

  // ─── Utility: UUID v4 ───────────────────────────────────────
  function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  // ─── Utility: Cookie ───────────────────────────────────────
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + d.toUTCString() +
      ';path=/;SameSite=Lax';
  }

  // ─── Visitor ID (persistent, 1-year cookie) ────────────────
  function getOrCreateVisitorId() {
    var vid = getCookie(COOKIE_NAME);
    if (!vid) {
      vid = uuid();
      setCookie(COOKIE_NAME, vid, COOKIE_DAYS);
    }
    return vid;
  }

  // ─── ThumbmarkJS Fingerprinting ──────────────────────────────
  // Generates a stable browser fingerprint for cross-session visitor identification.
  // Uses ThumbmarkJS (MIT) loaded from CDN — ~80% accuracy across sessions.
  function generateFingerprint(callback) {
    try {
      // Check if we already have a cached fingerprint (avoid recomputing)
      var cached = null;
      try { cached = localStorage.getItem('sl_fp'); } catch (e) {}
      if (cached) { callback(cached); return; }

      // Load ThumbmarkJS from CDN (lightweight, ~4KB gzipped)
      var script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@aspect-build/thumbmarkjs@0.14.8/dist/thumbmark.umd.js';
      script.async = true;
      script.onload = function () {
        if (window.ThumbmarkJS) {
          window.ThumbmarkJS.getFingerprint().then(function (fp) {
            try { localStorage.setItem('sl_fp', fp); } catch (e) {}
            callback(fp);
          }).catch(function () { callback(null); });
        } else {
          callback(null);
        }
      };
      script.onerror = function () { callback(null); };
      document.head.appendChild(script);
    } catch (e) {
      callback(null);
    }
  }

  // ─── Session Management ────────────────────────────────────
  function getOrCreateSession() {
    var sid = null;
    try {
      sid = sessionStorage.getItem(SESSION_KEY);
      var lastTs = parseInt(sessionStorage.getItem(SESSION_TS_KEY) || '0', 10);
      if (sid && (Date.now() - lastTs) < SESSION_TIMEOUT) {
        sessionStorage.setItem(SESSION_TS_KEY, String(Date.now()));
        return sid;
      }
    } catch (e) { /* sessionStorage blocked */ }

    sid = uuid();
    try {
      sessionStorage.setItem(SESSION_KEY, sid);
      sessionStorage.setItem(SESSION_TS_KEY, String(Date.now()));
    } catch (e) { }
    return sid;
  }

  // ─── UTM Extraction ────────────────────────────────────────
  function extractUTMs() {
    // Check sessionStorage first (persists across internal navigation)
    try {
      var cached = sessionStorage.getItem(UTM_KEY);
      if (cached) return JSON.parse(cached);
    } catch (e) { }

    var params = new URLSearchParams(window.location.search);
    var utm = {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
      term: params.get('utm_term') || '',
      content: params.get('utm_content') || '',
      gclid: params.get('gclid') || '',
      fbclid: params.get('fbclid') || '',
    };

    // Only store if at least one param exists
    if (utm.source || utm.medium || utm.campaign || utm.gclid || utm.fbclid) {
      try { sessionStorage.setItem(UTM_KEY, JSON.stringify(utm)); } catch (e) { }
    }

    return utm;
  }

  // ─── Traffic Source Classification ─────────────────────────
  function classifyTrafficSource(ref, utm) {
    if (utm.gclid || (utm.source && utm.medium === 'cpc') || utm.medium === 'paid') return 'paid';
    if (utm.fbclid || utm.source === 'facebook' || utm.source === 'instagram' || utm.source === 'meta') return 'social';
    if (utm.source) {
      if (utm.medium === 'email') return 'email';
      if (utm.medium === 'social') return 'social';
      return 'paid';
    }
    if (!ref) return 'direct';

    var refDomain = '';
    try { refDomain = new URL(ref).hostname; } catch (e) { return 'direct'; }
    var currentDomain = window.location.hostname;
    if (refDomain === currentDomain) return 'direct';

    // Search engines
    var searchEngines = ['google', 'bing', 'yahoo', 'duckduckgo', 'baidu', 'yandex'];
    for (var i = 0; i < searchEngines.length; i++) {
      if (refDomain.indexOf(searchEngines[i]) !== -1) return 'organic';
    }

    // Social platforms
    var social = ['facebook', 'instagram', 'twitter', 'linkedin', 'pinterest', 'reddit', 'tiktok', 'youtube', 't.co'];
    for (var i = 0; i < social.length; i++) {
      if (refDomain.indexOf(social[i]) !== -1) return 'social';
    }

    return 'referral';
  }

  // ─── Device Info ───────────────────────────────────────────
  function getDeviceInfo() {
    var ua = navigator.userAgent;
    var browser = 'Other';
    if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) browser = 'Chrome';
    else if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) browser = 'Safari';
    else if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
    else if (ua.indexOf('Edg') > -1) browser = 'Edge';
    else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) browser = 'Opera';

    var os = 'Other';
    if (ua.indexOf('Windows') > -1) os = 'Windows';
    else if (ua.indexOf('Mac') > -1) os = 'macOS';
    else if (ua.indexOf('Linux') > -1) os = 'Linux';
    else if (ua.indexOf('Android') > -1) os = 'Android';
    else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';

    var type = 'desktop';
    if (/Mobi|Android/i.test(ua)) type = 'mobile';
    else if (/Tablet|iPad/i.test(ua)) type = 'tablet';

    return {
      type: type,
      browser: browser,
      os: os,
      screenResolution: screen.width + 'x' + screen.height,
      language: (navigator.language || navigator.userLanguage || 'en').substring(0, 10),
    };
  }

  // ─── Referrer Domain ───────────────────────────────────────
  function getReferrerDomain() {
    try {
      if (!document.referrer) return '';
      return new URL(document.referrer).hostname;
    } catch (e) { return ''; }
  }

  // ─── Event Queue ───────────────────────────────────────────
  function queueEvent(type, category, data) {
    eventQueue.push({
      type: type,
      category: category || 'engagement',
      data: data || {},
      timestamp: new Date().toISOString(),
    });
  }

  // ─── Shopify Customer Detection ────────────────────────────
  // Detects logged-in Shopify customers so we can identify visitors
  function detectCustomer() {
    try {
      // Method 0: full identity injected by the sl-tracker.liquid snippet from the
      // Liquid {{ customer }} object. Preferred — the methods below only ever yield
      // an ID, which is useless until that customer exists in our local DB.
      if (window.__SL_CUSTOMER && (window.__SL_CUSTOMER.shopify_customer_id || window.__SL_CUSTOMER.email)) {
        return window.__SL_CUSTOMER;
      }
      // Method 1: ShopifyAnalytics meta (most reliable)
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
        var cid = window.ShopifyAnalytics.meta.page.customerId;
        if (cid) return { shopify_customer_id: String(cid) };
      }
      // Method 2: __st cookie (Shopify analytics)
      if (window.__st && window.__st.cid) {
        return { shopify_customer_id: String(window.__st.cid) };
      }
      // Method 3: meta tag (some themes expose it)
      var meta = document.querySelector('meta[name="shopify-customer-id"]');
      if (meta && meta.getAttribute('content')) {
        return { shopify_customer_id: meta.getAttribute('content') };
      }
    } catch (e) { }
    return null;
  }

  function buildPayload() {
    var payload = {
      visitor_id: visitorId,
      session_id: sessionId,
      fingerprint: fingerprintId,
      device: deviceInfo,
      referrer: document.referrer || '',
      referrer_domain: getReferrerDomain(),
      traffic_source: trafficSource,
      utm: utmData,
      events: eventQueue.splice(0),
    };
    if (customerData) {
      payload.customer = customerData;
    }
    return payload;
  }

  var HEADERS = {
    'Content-Type': 'application/json',
  };

  function flushEvents() {
    if (eventQueue.length === 0) return;
    var payload = buildPayload();
    try {
      fetch(ENDPOINT + '/ingest', {
        method: 'POST',
        headers: HEADERS,
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () { });
    } catch (e) { }
  }

  function flushBeacon(extraEvents) {
    if (extraEvents) {
      for (var i = 0; i < extraEvents.length; i++) {
        eventQueue.push(extraEvents[i]);
      }
    }
    if (eventQueue.length === 0) return;
    var payload = buildPayload();
    // Use fetch with keepalive (more reliable with ngrok headers than sendBeacon)
    try {
      fetch(ENDPOINT + '/ingest', {
        method: 'POST',
        headers: HEADERS,
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () { });
    } catch (e) {
      // Last resort: sendBeacon (no custom headers, may hit ngrok interstitial)
      try {
        var blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        navigator.sendBeacon(ENDPOINT + '/ingest', blob);
      } catch (e2) { }
    }
  }

  // ─── Page View Tracking ────────────────────────────────────
  function trackPageView() {
    // Update time on previous page
    if (pageEntryTime && currentPageUrl) {
      var timeSpent = Math.round((Date.now() - pageEntryTime) / 1000);
      queueEvent('page_exit', 'navigation', {
        url: currentPageUrl,
        path: currentPageUrl ? new URL(currentPageUrl).pathname : '',
        time_on_page: timeSpent,
        scroll_depth: getMaxScrollDepth(),
      });
    }

    pageEntryTime = Date.now();
    currentPageUrl = window.location.href;
    scrollMilestones = {};

    queueEvent('page_view', 'navigation', {
      url: window.location.href,
      path: window.location.pathname,
      title: document.title,
      referrer: document.referrer || '',
    });
  }

  // ─── Scroll Depth Tracking ────────────────────────────────
  var maxScrollPct = 0;

  function getMaxScrollDepth() {
    return maxScrollPct;
  }

  function onScroll() {
    var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    var docHeight = Math.max(
      document.body.scrollHeight, document.documentElement.scrollHeight,
      document.body.offsetHeight, document.documentElement.offsetHeight
    );
    var winHeight = window.innerHeight;
    if (docHeight <= winHeight) return;

    var pct = Math.round((scrollTop + winHeight) / docHeight * 100);
    if (pct > maxScrollPct) maxScrollPct = pct;

    for (var i = 0; i < SCROLL_THRESHOLDS.length; i++) {
      var threshold = SCROLL_THRESHOLDS[i];
      if (pct >= threshold && !scrollMilestones[threshold]) {
        scrollMilestones[threshold] = true;
        queueEvent('scroll_milestone', 'engagement', {
          depth: threshold,
          page_url: window.location.href,
          page_path: window.location.pathname,
        });
      }
    }
  }

  // ─── Exit Intent Detection ────────────────────────────────
  function onMouseOut(e) {
    if (exitIntentFired) return;
    if (e.clientY <= 0) {
      exitIntentFired = true;
      queueEvent('exit_intent', 'engagement', {
        page_url: window.location.href,
        page_path: window.location.pathname,
      });
    }
  }

  // ─── E-commerce Event Hooks ───────────────────────────────

  // Helper: attach click listener with duplicate guard
  function trackCheckoutClick(selector, checkoutType, useQueryAll) {
    try {
      var els = useQueryAll
        ? document.querySelectorAll(selector)
        : (function () { var el = typeof selector === 'string' ? document.querySelector(selector) : selector; return el ? [el] : []; })();
      for (var i = 0; i < els.length; i++) {
        if (els[i]._slTracked) continue;
        els[i]._slTracked = true;
        (function (el, type) {
          el.addEventListener('click', function () {
            // Buy Now / direct checkout buttons skip cart — fire add_to_cart too
            queueEvent('add_to_cart', 'ecommerce', {
              page_url: window.location.href,
              page_path: window.location.pathname,
              source: 'buy_now_' + type,
            });
            queueEvent('checkout_started', 'ecommerce', {
              page_url: window.location.href,
              page_path: window.location.pathname,
              checkout_type: type,
            });
            flushEvents();
          });
        })(els[i], checkoutType);
      }
    } catch (e) { }
  }

  function initEcommerceTracking() {

    // ── 1. Product View Detection ──
    if (window.location.pathname.indexOf('/products/') !== -1) {
      var productTitle = '';
      var priceEl = null;
      try {
        var titleEl = document.querySelector('.product-single__title, .product__title, h1.title, h1');
        if (titleEl) productTitle = titleEl.textContent.trim();
        priceEl = document.querySelector('.product-single__price, .product__price, .price, [data-product-price]');
      } catch (e) { }
      queueEvent('product_viewed', 'ecommerce', {
        page_url: window.location.href,
        page_path: window.location.pathname,
        product_title: productTitle,
        product_handle: window.location.pathname.split('/products/')[1]?.split('?')[0] || '',
        price: priceEl ? priceEl.textContent.trim() : '',
      });
    }

    // ── 2. Add to Cart — intercept fetch/XHR/form to /cart/add ──
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');
      if (urlStr.indexOf('/cart/add') !== -1) {
        try {
          var body = arguments[1] && arguments[1].body;
          var data = {};
          if (body) {
            try { data = JSON.parse(body); } catch (e) {
              if (body instanceof FormData) {
                data = {};
                body.forEach(function(v, k) { data[k] = v; });
              }
            }
          }
          queueEvent('add_to_cart', 'ecommerce', {
            page_url: window.location.href,
            page_path: window.location.pathname,
            product_id: data.id || (data.items && data.items[0] && data.items[0].id) || '',
            quantity: data.quantity || (data.items && data.items[0] && data.items[0].quantity) || 1,
          });
        } catch (e) { }
      }
      return origFetch.apply(this, arguments);
    };

    var origXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._slUrl = url;
      return origXHROpen.apply(this, arguments);
    };
    var origXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (body) {
      if (this._slUrl && this._slUrl.indexOf('/cart/add') !== -1) {
        queueEvent('add_to_cart', 'ecommerce', {
          page_url: window.location.href,
          page_path: window.location.pathname,
        });
      }
      return origXHRSend.apply(this, arguments);
    };

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form.action && form.action.indexOf('/cart/add') !== -1) {
        queueEvent('add_to_cart', 'ecommerce', {
          page_url: window.location.href,
          page_path: window.location.pathname,
        });
      }
    });

    // ── 3. Gokwik Checkout (Indian customers) ──
    // Buy Now button on product page
    trackCheckoutClick('#gokwik-buy-now', 'gokwik_buy_now');
    // Cart checkout button(s)
    trackCheckoutClick('.gokwik-checkout button', 'gokwik_cart', true);

    // Gokwik modal close — track abandonment vs completion
    function hookGokwikModal() {
      if (window.gokwikSdk && typeof window.gokwikSdk.on === 'function') {
        window.gokwikSdk.on('modal_closed', function () {
          queueEvent('checkout_modal_closed', 'ecommerce', {
            page_url: window.location.href,
            page_path: window.location.pathname,
            checkout_type: 'gokwik',
          });
        });
        return true;
      }
      return false;
    }
    if (!hookGokwikModal()) {
      window.addEventListener('gokwikLoaded', hookGokwikModal);
    }

    // ── 4. Shopify Native Checkout (international customers) ──
    // Standard checkout button (.shopifycheckoutbtn)
    trackCheckoutClick('.shopifycheckoutbtn', 'shopify', true);

    // Cart form submission to /checkout
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form.action && (form.action.indexOf('/checkout') !== -1 || form.action.indexOf('checkout.shopify.com') !== -1)) {
        queueEvent('checkout_started', 'ecommerce', {
          page_url: window.location.href,
          page_path: window.location.pathname,
          checkout_type: 'shopify',
        });
        flushEvents();
      }
    });

    // Links to /checkout
    document.addEventListener('click', function (e) {
      var el = e.target;
      for (var i = 0; i < 5 && el && el !== document; i++) {
        if (el.tagName === 'A' && el.href && el.href.indexOf('/checkout') !== -1) {
          queueEvent('checkout_started', 'ecommerce', {
            page_url: window.location.href,
            page_path: window.location.pathname,
            checkout_type: 'shopify_link',
          });
          flushEvents();
          return;
        }
        el = el.parentElement;
      }
    });

    // ── 5. Shopify Accelerated Checkout (Shop Pay, Google Pay, Apple Pay) ──
    // These buttons are inside shadow DOM or iframes, so we use a MutationObserver
    // to detect when they're clicked (the accelerated-checkout container gets interacted with)
    trackCheckoutClick('.shopify-payment-button__button', 'shopify_dynamic_buy', true);
    trackCheckoutClick('[data-shopify="payment-button"]', 'shopify_dynamic_buy', true);

    // Shop Pay / wallet buttons load dynamically — observe and hook
    var acObserver = new MutationObserver(function (mutations) {
      trackCheckoutClick('.shopify-payment-button__button', 'shopify_dynamic_buy', true);
      trackCheckoutClick('[data-shopify="payment-button"]', 'shopify_dynamic_buy', true);
      // Gokwik buttons can also load dynamically (cart drawer)
      trackCheckoutClick('.gokwik-checkout button', 'gokwik_cart', true);
      trackCheckoutClick('#gokwik-buy-now', 'gokwik_buy_now');
    });
    acObserver.observe(document.body, { childList: true, subtree: true });
    // Stop observing after 30s to avoid perf impact
    setTimeout(function () { acObserver.disconnect(); }, 30000);

    // ── 6. Checkout page detection (if user lands on /checkouts on this domain) ──
    if (window.location.pathname.indexOf('/checkouts') !== -1) {
      queueEvent('add_to_cart', 'ecommerce', {
        page_url: window.location.href,
        page_path: window.location.pathname,
        source: 'checkout_page',
      });
      queueEvent('checkout_started', 'ecommerce', {
        page_url: window.location.href,
        page_path: window.location.pathname,
        checkout_type: 'shopify',
      });
    }

    // ── 7. Purchase Completion Detection ──
    // After Gokwik/Shopify payment, customer lands on thank_you or order status page
    var loc = window.location;
    var isThankYou = loc.pathname.indexOf('/thank_you') !== -1;
    var isOrderStatus = loc.pathname.indexOf('/orders/') !== -1 && loc.pathname.indexOf('/account') === -1;
    var isCheckoutThankYou = loc.pathname.indexOf('/checkouts/') !== -1 && loc.search.indexOf('step=thank_you') !== -1;
    var hasCheckoutObj = window.Shopify && window.Shopify.checkout && window.Shopify.checkout.order_id;

    if (isThankYou || isOrderStatus || isCheckoutThankYou || hasCheckoutObj) {
      var orderData = {};
      try {
        if (window.Shopify && window.Shopify.checkout) {
          var co = window.Shopify.checkout;
          orderData = {
            order_id: co.order_id || '',
            order_number: co.order_number || '',
            value: parseFloat(co.total_price) || 0,
            subtotal: parseFloat(co.subtotal_price) || 0,
            shipping: parseFloat(co.shipping_rate && co.shipping_rate.price) || 0,
            tax: parseFloat(co.total_tax) || 0,
            currency: co.currency || 'INR',
            item_count: co.line_items ? co.line_items.length : 0,
            discount: co.discount ? co.discount.code : '',
            payment_gateway: co.payment_gateway_names ? co.payment_gateway_names.join(',') : '',
          };
        }
      } catch (e) { }
      // Purchase implies add_to_cart + checkout (ensure funnel consistency)
      queueEvent('add_to_cart', 'ecommerce', {
        page_url: window.location.href,
        page_path: window.location.pathname,
        source: 'purchase_page',
      });
      queueEvent('checkout_started', 'ecommerce', {
        page_url: window.location.href,
        page_path: window.location.pathname,
        checkout_type: 'completed',
      });
      queueEvent('checkout_completed', 'ecommerce', {
        page_url: window.location.href,
        page_path: window.location.pathname,
        ...orderData,
      });
      flushEvents();
    }

    // ── 8. Cart View Detection ──
    if (window.location.pathname === '/cart') {
      queueEvent('cart_viewed', 'ecommerce', {
        page_url: window.location.href,
        page_path: window.location.pathname,
      });
    }

    // ── 9. Collection / Category Browsing ──
    if (window.location.pathname.indexOf('/collections/') !== -1) {
      var collHandle = window.location.pathname.split('/collections/')[1];
      if (collHandle) collHandle = collHandle.split('/')[0].split('?')[0];
      queueEvent('collection_viewed', 'ecommerce', {
        page_url: window.location.href,
        page_path: window.location.pathname,
        collection_handle: collHandle || '',
      });
    }

    // ── 10. Search Detection ──
    var searchParam = new URLSearchParams(window.location.search).get('q');
    if (searchParam && window.location.pathname.indexOf('/search') !== -1) {
      queueEvent('search', 'engagement', {
        query: searchParam,
        page_url: window.location.href,
        page_path: window.location.pathname,
      });
    }
  }

  // ─── Unload / Exit Tracking ───────────────────────────────
  function onUnload() {
    // Calculate final time on page
    var timeSpent = pageEntryTime ? Math.round((Date.now() - pageEntryTime) / 1000) : 0;

    // Send end-session beacon
    try {
      var endPayload = {
        session_id: sessionId,
        visitor_id: visitorId,
        exit_page: window.location.href,
        scroll_depth: maxScrollPct,
        time_on_page: timeSpent,
        timestamp: new Date().toISOString(),
      };
      var blob = new Blob([JSON.stringify(endPayload)], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT + '/end-session', blob);
    } catch (e) { }

    // Flush remaining events
    flushBeacon();
  }

  // ─── Click Tracking (important elements) ──────────────────
  function onDocClick(e) {
    var el = e.target;
    // Walk up to find meaningful element
    for (var i = 0; i < 5 && el && el !== document; i++) {
      if (el.tagName === 'A' || el.tagName === 'BUTTON' || el.type === 'submit') break;
      el = el.parentElement;
    }
    if (!el || el === document) return;

    var tag = el.tagName;
    if (tag !== 'A' && tag !== 'BUTTON' && el.type !== 'submit') return;

    queueEvent('click', 'engagement', {
      tag: tag,
      text: (el.textContent || '').trim().substring(0, 100),
      href: el.href || '',
      id: el.id || '',
      class: (el.className || '').toString().substring(0, 100),
      page_url: window.location.href,
      page_path: window.location.pathname,
    });
  }

  // ─── SPA Navigation Detection ────────────────────────────
  function hookSPANavigation() {
    var origPushState = history.pushState;
    history.pushState = function () {
      origPushState.apply(this, arguments);
      setTimeout(trackPageView, 50);
    };
    var origReplaceState = history.replaceState;
    history.replaceState = function () {
      origReplaceState.apply(this, arguments);
      // Don't track replaceState as new page view (usually URL cleanup)
    };
    window.addEventListener('popstate', function () {
      setTimeout(trackPageView, 50);
    });
  }

  // ─── Throttle helper ──────────────────────────────────────
  function throttle(fn, ms) {
    var last = 0;
    return function () {
      var now = Date.now();
      if (now - last >= ms) {
        last = now;
        fn.apply(this, arguments);
      }
    };
  }

  // ─── Initialize ───────────────────────────────────────────
  function init() {
    if (initialized) return;
    initialized = true;

    visitorId = getOrCreateVisitorId();
    sessionId = getOrCreateSession();
    deviceInfo = getDeviceInfo();
    utmData = extractUTMs();
    trafficSource = classifyTrafficSource(document.referrer, utmData);
    customerData = detectCustomer();

    // Generate browser fingerprint (async, non-blocking)
    generateFingerprint(function (fp) {
      if (fp) fingerprintId = fp;
    });

    // Track first page view
    trackPageView();

    // E-commerce hooks
    initEcommerceTracking();

    // Scroll depth (throttled to 250ms)
    window.addEventListener('scroll', throttle(onScroll, 250), { passive: true });

    // Exit intent (desktop only)
    if (deviceInfo.type === 'desktop') {
      document.addEventListener('mouseout', onMouseOut);
    }

    // Click tracking
    document.addEventListener('click', onDocClick, true);

    // SPA navigation
    hookSPANavigation();

    // Unload tracking
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') onUnload();
    });
    window.addEventListener('beforeunload', onUnload);

    // Periodic flush
    setInterval(flushEvents, FLUSH_INTERVAL);

    // Initial flush after 2 seconds (ensures first page view is sent quickly)
    setTimeout(flushEvents, 2000);
  }

  // ─── Non-blocking start ───────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
